import Events from '../lib/events.js';
import { t9n } from '../lib/translate.js';
import { TS } from '../config/translation-keys.js';
import APIClient from '../state-charts/api-client';

export default class BuyMachine {
  constructor(options) {
    this.make_context(options);
    this.add_computed_context();
    this.get_payment_options().then(opts => {
      this.context.payment_methods = opts;
    });
  }

  make_context(options) {
    this.context = {
      // the original context
      ...options.context,

      // static/parent data
      id: options.id,
      main: options.main,
      minimum_amount: options.main.context.pp_minimum,
      multiplier: options.main.context.pp_multiplier,
      currency_sign: options.main.context.currency_sign,
      amount: options.main.context.amount,
      nets_host: options.main.context.nets_host,

      // variables
      buy_amount: '',
      buy_price: 0.0,
      credit_card_id: 0,
      payment_type: 'payment_type_billing',
      payment_methods: null,
      payment_method: null,
      error: '',
      show_confirm: false,
      confirmed: false,
      valid: false,
      show_error: false,
      show_payment_choices: false,
      processing: false,
      show_payment_window: false,
      transaction_id: null,
      transaction_callback: null,
    };
  }

  add_computed_context() {
    // computed variables
    this.context.buy_price = Math.floor(
      Number(this.context.buy_amount) / this.context.multiplier
    );

    this.context.cash_amount = Number(
      this.context.amount / this.context.multiplier
    ).toFixed(2);

    this.context.amount_text = `${this.context.amount} (${this.context.cash_amount} ${this.context.currency_sign})`;

    // translations
    this.context.form_title = t9n(TS.PILOT_POINTS_BUY_FORM_TITLE);

    this.context.explanation_1 = t9n(TS.PILOT_POINTS_BUY_EXPLANATION_LINE1);

    this.context.explanation_2 = t9n(TS.PILOT_POINTS_BUY_EXPLANATION_LINE2);

    this.context.explanation_3 = t9n(TS.PILOT_POINTS_BUY_EXPLANATION_LINE3);

    this.context.your_pp_label = t9n(TS.PILOT_POINTS_BUY_YOUR_PP_LABEL);

    this.context.buy_amount_label = t9n(TS.PILOT_POINTS_BUY_BUY_AMOUNT_LABEL);

    this.context.buy_label = t9n(TS.PILOT_POINTS_BUY_BUY_LABEL);

    this.context.confirm_yes_label = t9n(TS.PILOT_POINTS_BUY_CONFIRM_YES_LABEL);

    this.context.confirm_no_label = t9n(
      TS.PILOT_POINTS_BUY_CONFIRM_CANCEL_LABEL
    );

    this.context.confirm_title = t9n(TS.PILOT_POINTS_BUY_CONFIRM_TITLE);

    this.context.confirm_text = t9n(
      TS.PILOT_POINTS_BUY_CONFIRM_TEXT,
      this.context.buy_amount,
      this.context.buy_price,
      this.context.currency_sign
    );

    this.context.total_text = t9n(
      TS.PILOT_POINTS_BUY_TOTAL_TEXT,
      Number(this.context.buy_price).toFixed(2),
      this.context.currency_sign
    );

    this.context.amount_is_nan_text = t9n(
      TS.PILOT_POINTS_BUY_ERROR_AMOUNT_IS_NAN
    );

    this.context.amount_too_low_text = t9n(
      TS.PILOT_POINTS_BUY_ERROR_AMOUNT_TOO_LOW,
      this.context.minimum_amount
    );

    this.context.payment_failure_text = t9n(TS.PILOT_POINTS_BUY_PAYMENT_FAILED);

    this.context.payment_success_text = t9n(
      TS.PILOT_POINTS_BUY_SUCCESS_TEXT,
      this.context.buy_amount
    );
  }

  update_context(context) {
    this.context = { ...this.context, ...context };
    this.add_computed_context();
  }

  payment_data() {
    let data = {
      amount: this.context.buy_amount,
      app: true,
      payment_type: this.context.payment_method.payment_type,
    };

    if (data.payment_type === 'payment_type_credit_card') {
      data.credit_card_id = this.context.payment_method.data.credit_card.credit_card_id;
    } else {
      data.credit_card_id = 0;
    }

    return data;
  }

  async get_payment_options() {
    let data = await APIClient.get_payment_options({
      price: this.context.buy_price,
    });

    if (data && 'options' in data) {
      data?.options.map(item => {
        item.id = data.options.indexOf(item) + 1;
      });
    } else {
      data = { options: [] };
    }

    return data.options;
  }

  set_payment_method(e) {
    if (e && e.detail && e.detail.payment_method) {
      this.context.payment_method = e.detail.payment_method;
    }
  }

  update_amount(amount) {
    Events.dispatch('ppamountchanged', { amount: amount });
  }

  validate() {
    if (!!this.context.buy_amount) {
      let num = Number(this.context.buy_amount);

      if (Number.isNaN(num)) {
        this.context.error = this.context.amount_is_nan_text;
        this.context.show_error = true;
        this.context.valid = false;
      } else if (num < this.context.minimum_amount) {
        this.context.error = this.context.amount_too_low_text;
        this.context.show_error = true;
        this.context.valid = false;
      } else {
        this.context.error = '';
        this.context.show_error = false;
        this.context.valid = true;
      }
    } else {
      this.context.error = '';
      this.context.show_error = false;
      this.context.valid = true;
    }
  }

  process_confirmed() {
    this.context.show_confirm = false;
    this.context.confirmed = true;

    if (this.context.valid && this.context.confirmed) {
      this.context.show_payment_choices = true;
    } else {
      this.context.show_error = true;
    }
  }

  async finalize_transaction(transaction_id) {
    let result = null;
    let api_response = null;

    this.context.processing = false;

    try {
      api_response = await APIClient.finalize_buy_pilot_points({
        amount: this.context.buy_amount,
        reference: transaction_id,
        product_amount: this.context.product_amount,
        product_orderer_price: this.context.product_orderer_price,
      });
    } catch (e) {
      console.log('Error when finalizing payment:', e);
    }

    if (api_response && 'pilot_points' in api_response) {
      result = {
        type: 'success',
        message: this.context.payment_success_text,
      };
      this.update_amount(api_response.pilot_points);
    } else {
      result = {
        type: 'error',
        message: this.context.payment_failure_text,
      };
    }

    return result;
  }

  async buy() {
    try {
      this.context.processing = true;

      const api_response = await APIClient.init_buy_pilot_points(
        this.payment_data()
      );

      if (api_response) {
        this.context.transaction_id = api_response.reference;
        this.context.show_payment_window = true;
      }

      // now we are just waiting to finalize_transaction would be called by the
      // payment form...
      return;
    } catch (e) {
      console.log('Error when initializing payment:', e);
    }
  }

  reset_state() {
    this.context.confirmed = false;
    this.context.show_confirm = false;
    this.context.valid = false;
    this.context.show_error = false;
    this.context.error = '';
    this.context.processing = false;
    this.context.show_payment_choices = false;
    this.context.buy_amount = '';
    this.context.processing = false;
    this.context.show_payment_window = false;
    this.context.transaction_id = null;
    this.context.transaction_callback = null;
    this.update_context();
  }
}
